import React, { useState, useEffect, useCallback } from "react";
import { httpService } from "../../services/httpService";
import LoaderSpiner from "../../components/LoaderSpiner/LoaderSpiner";
import DynamicTable from "../common/DynamicTable";
import { useNavigate } from "react-router-dom";
import { useSidebar } from "../../context/SidebarContext";
import { useLocation } from "react-router-dom";
import { IoMdEye } from "react-icons/io";
import { FaCreditCard } from "react-icons/fa";
import SupplierPaymentDrawer from "../../components/inventory/SupplierPaymentDrawer";
import { toast } from "react-toastify";

const PurchaseBill = ({ supplierId }) => {
  const [purchases, setPurchases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 4;
  const { setShowSidebar } = useSidebar();
  const location = useLocation();
  const [showAddPaymentDrawer, setShowAddPaymentDrawer] = useState(false);
  const [selectedPurchaseBill, setSelectedPurchaseBill] = useState(null);

  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentRows = purchases.slice(indexOfFirstRow, indexOfLastRow);
  const totalPages = Math.ceil(purchases.length / rowsPerPage);

  // Create a reusable fetch function
  const fetchPurchaseData = useCallback(async () => {
    if (!supplierId) {
      setError("No supplierId provided. (supplierId is: " + String(supplierId) + ")");
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const response = await httpService.get(
        `/vendorpurchase/getVendorSupplierPurchases/${supplierId}`
      );
      
      if (response.status && response.code === 200) {
        // Transform the data to include correct payment calculations
        const transformedPurchases = response.data.map(purchase => {
          // Calculate correct paid amount (use total_amount if status is paid)
          const paidAmount = purchase.payment_status === 'paid'
            ? purchase.total_amount
            : purchase.paid_amount || 0;

          // Calculate due amount
          const dueAmount = purchase.total_amount - paidAmount;

          return {
            ...purchase,
            calculated_paid: paidAmount,
            calculated_due: dueAmount,
            calculated_status: purchase.payment_status
          };
        });

        setPurchases(transformedPurchases);
      } else {
        setError(response.message || "Failed to fetch purchase data");
      }
    } catch (err) {
      setError(err.message || "An error occurred while fetching data");
    } finally {
      setLoading(false);
    }
  }, [supplierId]);

  // Use the fetch function in useEffect
  useEffect(() => {
    fetchPurchaseData();
  }, [fetchPurchaseData]);

  useEffect(() => {
    if (location.pathname.startsWith("/admin/inventory")) {
      setShowSidebar(true);
    }
  }, [location.pathname, setShowSidebar]);

  const columns = [
    {
      key: "invoice_number",
      header: "Invoice #",
      sortable: true
    },
    {
      key: "invoice_date",
      header: "Date",
      sortable: true,
      renderCell: row => {
        if (!row.invoice_date) return "-";
        const date = new Date(row.invoice_date);
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
      }
    },
    {
      key: "num_products",
      header: "# of Products",
      sortable: false,
      renderCell: row => Array.isArray(row.items) ? row.items.length : "-"
    },
    {
      key: "amount",
      header: "Base Amount",
      sortable: true,
      renderCell: row => `₹${row.amount || 0}`
    },
    {
      key: "total_gst",
      header: "GST",
      sortable: true,
      renderCell: row => `₹${row.total_gst || 0}`
    },
    {
      key: "total_amount",
      header: "Total",
      sortable: true,
      renderCell: row => `₹${row.total_amount?.toFixed(2) || 0}`
    },
    {
      key: "calculated_paid",
      header: "Paid",
      sortable: true,
      renderCell: row => (
        <span className="text-green-600">
          ₹{row.calculated_paid?.toFixed(2) || 0}
        </span>
      )
    },
    {
      key: "calculated_due",
      header: "Due",
      sortable: true,
      renderCell: row => (
        <span className={`${row.calculated_due > 0 ? 'text-red-600' : 'text-green-600'}`}>
          ₹{Math.max(0, row.calculated_due).toFixed(2)}
        </span>
      )
    },
    {
      key: "calculated_status",
      header: "Status",
      sortable: true,
      renderCell: row => {
        // Convert status to proper case (first letter uppercase)
        const status = row.calculated_status || 'unknown';
        const displayStatus = status.charAt(0).toUpperCase() + status.slice(1);
        
        // Determine background color based on status
        let bgColorClass = 'bg-gray-100 text-gray-700'; // default
        if (status === 'paid') {
          bgColorClass = 'bg-green-100 text-green-700';
        } else if (status === 'unpaid') {
          bgColorClass = 'bg-red-100 text-red-700';
        } else if (status === 'partial') {
          bgColorClass = 'bg-yellow-100 text-yellow-700';
        }
        
        return (
          <span className={`inline-block px-2 py-1 rounded-full text-xs font-semibold ${bgColorClass}`}>
            {displayStatus}
          </span>
        );
      }
    },
  ];

  const actions = [
    {
      label: "View",
      icon: (
        <IoMdEye className="w-5 h-5 text-gray-600 group-hover:text-yellow-600 transition-colors" />
      ),
      onClick: (row) =>
        navigate(
          `/admin/inventory-management/suppliers/${supplierId}/purchase_bills/${row._id}`,
          {
            state: { from: "supplier", supplierId },
          }
        ),
      colorClasses: "hover:border-yellow-500 hover:bg-yellow-50",
    },
    {
      label: "Pay",
      icon: (
        <FaCreditCard className="w-5 h-5 text-gray-600 group-hover:text-green-600 transition-colors" />
      ),
      onClick: (row) => {
        setSelectedPurchaseBill(row);
        setShowAddPaymentDrawer(true);
      },
      colorClasses: "hover:border-green-500 hover:bg-green-50",
      // Only show for unpaid or partially paid bills with due amount > 0
      shouldShow: (row) => row.payment_status !== 'paid' && row.calculated_due > 0
    },
  ];

  const handlePaymentSuccess = (savedPayment) => {
    
    // Refresh the purchase bills data to show updated payment status
    fetchPurchaseData();
    
    // Show success toast
    toast.success("Payment recorded successfully");
    
    // Close the drawer (this will be handled by the onClose prop as well)
    setShowAddPaymentDrawer(false);
    setSelectedPurchaseBill(null);
  };

  const handlePaymentDrawerClose = () => {
    setShowAddPaymentDrawer(false);
    setSelectedPurchaseBill(null);
  };

  if (!supplierId) {
    return (
      <div className="p-6 text-red-500 bg-white rounded shadow">
        No supplier selected. (supplierId is: {String(supplierId)})
      </div>
    );
  }

  if (loading) {
    return <LoaderSpiner text="Loading purchase bills..." />;
  }

  if (error) {
    return (
      <div className="p-6 text-red-500 bg-white rounded shadow">
        Error: {error}
      </div>
    );
  }

  return (
    <div className="bg-white p-4 min-h-[400px]">
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-xl font-semibold text-slate-700">Purchase Bills</h3>
        <button
          className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-2 rounded-full shadow"
          onClick={() =>
            navigate(
              `/admin/inventory-management/purchase_bills/new?supplierId=${supplierId}`,
              {
                state: { supplierName: purchases[0]?.supplier_name || "" },
              }
            )
          }
        >
          + Add Purchase Bill
        </button>
      </div>

      <div className="bg-white rounded-xl shadow p-6 border border-gray-100">
        {/* SHOW MESSAGE ONLY WHEN NO DATA */}
        {purchases.length === 0 && (
          <div className="mb-4 text-gray-500 italic text-center py-8">
            No purchase bills found for this supplier.
          </div>
        )}

        {/* TABLE IS ALWAYS SHOWN */}
        <DynamicTable
          columns={columns}
          data={currentRows} // [] when no data
          actions={actions}
          className="w-full"
        />

        {/* PAGINATION ONLY WHEN DATA EXISTS */}
        {purchases.length > 0 && (
          <div className="flex justify-center items-center gap-3 mt-6">
            <button
              onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
              disabled={currentPage === 1}
              className="px-3 py-1 border rounded disabled:opacity-50 hover:bg-gray-50 transition-colors"
            >
              Prev
            </button>

            <span className="text-gray-600">
              Page {currentPage} of {totalPages}
            </span>

            <button
              onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
              disabled={currentPage === totalPages}
              className="px-3 py-1 border rounded disabled:opacity-50 hover:bg-gray-50 transition-colors"
            >
              Next
            </button>
          </div>
        )}

        {/* PAYMENT DRAWER */}
        <SupplierPaymentDrawer
          isOpen={showAddPaymentDrawer}
          supplierId={supplierId}
          purchaseBillId={selectedPurchaseBill?._id}
          onClose={handlePaymentDrawerClose}
          onSave={handlePaymentSuccess}
        />
      </div>
    </div>
  );
};

export default PurchaseBill;